import { useEffect } from "react";
import { createPortal } from "react-dom";
import { ActionButton } from "../../../components/Buttons";
import { SHARE_INTRO_OPTIONS } from "./shareIntroOptions";
import ShareIntroOptionCard from "./ShareIntroOptionCard";

function LoadingDot() {
  return (
    <span
      aria-hidden="true"
      className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

function ShareCardPreviewView({
  selectedIntroId,
  isPreparing,
  preparationFailed,
  shareError,
  onMessageChange,
  onOpenShareSheet,
  onPrepareShareLink,
  onCopyLink,
  onClose,
}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const bodyStyle = document.body.style;
    const rootStyle = document.documentElement.style;
    const previousScrollY = window.scrollY || window.pageYOffset || 0;
    const previousBodyOverflow = bodyStyle.overflow;
    const previousBodyPosition = bodyStyle.position;
    const previousBodyTop = bodyStyle.top;
    const previousBodyWidth = bodyStyle.width;
    const previousRootOverflow = rootStyle.overflow;

    bodyStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${previousScrollY}px`;
    bodyStyle.width = "100%";
    rootStyle.overflow = "hidden";

    return () => {
      bodyStyle.overflow = previousBodyOverflow;
      bodyStyle.position = previousBodyPosition;
      bodyStyle.top = previousBodyTop;
      bodyStyle.width = previousBodyWidth;
      rootStyle.overflow = previousRootOverflow;

      if (previousScrollY > 0 && typeof window.scrollTo === "function") {
        window.scrollTo(0, previousScrollY);
      }
    };
  }, []);

  const primaryLabel = isPreparing
    ? "Preparing card…"
    : preparationFailed
      ? "Try again"
      : "Share Card";
  const primaryDisabled = isPreparing;
  const copyDisabled = isPreparing || preparationFailed;
  const handlePrimaryAction = preparationFailed
    ? onPrepareShareLink
    : onOpenShareSheet;

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[80] flex min-h-[100dvh] items-end justify-center overflow-hidden px-2 pb-0 text-left sm:items-center sm:p-6">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close introduction options"
        className="share-sheet-overlay absolute inset-0 h-full w-full bg-black/38 backdrop-blur-[1px]"
      />

      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-sheet-title"
        className="share-sheet-panel relative z-10 w-full max-w-md overflow-hidden rounded-t-[1.5rem] bg-[#fffdf9] text-[#071015] shadow-[0_22px_70px_rgba(0,0,0,0.28)] sm:rounded-[1.5rem]"
      >
        <div className="max-h-[82dvh] overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 sm:max-h-[calc(100vh-3rem)] sm:px-5 sm:pb-5">
          <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-black/14 sm:mb-4" />

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1
                id="share-sheet-title"
                className="max-w-[17rem] text-xl font-black leading-tight text-[#071015] sm:text-2xl"
              >
                How would you like to introduce this card?
              </h1>
              <p className="mt-1.5 text-sm font-medium leading-snug text-[#5c6457]">
                Choose a message to send with this card
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-black/44 transition hover:bg-black/5 hover:text-black/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#087a34]"
            >
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          <div className="mt-6 space-y-2.5">
            {SHARE_INTRO_OPTIONS.map((option) => {
              const selected = option.id === selectedIntroId;

              return (
                <ShareIntroOptionCard
                  key={option.id}
                  option={option}
                  selected={selected}
                  onSelect={onMessageChange}
                />
              );
            })}
          </div>

          <div className="relative z-10 mt-4 space-y-2.5">
            <ActionButton
              type="button"
              onClick={handlePrimaryAction}
              disabled={primaryDisabled}
              fullWidth
              icon={isPreparing ? undefined : "share"}
            >
              <span className="inline-flex items-center justify-center gap-2">
                {isPreparing ? <LoadingDot /> : null}
                {primaryLabel}
              </span>
            </ActionButton>

            <button
              type="button"
              onClick={onCopyLink}
              disabled={copyDisabled}
              className="mx-auto flex min-h-[36px] items-center justify-center rounded-lg px-3 text-sm font-semibold text-[#307609]/72 transition hover:bg-[#307609]/[0.07] hover:text-[#245f07] disabled:pointer-events-none disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#087a34]"
            >
              Copy Link
            </button>
          </div>

          {shareError ? (
            <p className="relative z-10 mt-3 text-center text-sm font-bold text-red-600">
              {shareError}
            </p>
          ) : null}
        </div>
      </section>
    </div>,
    document.body
  );
}

export default ShareCardPreviewView;
